import { useAssertedParams } from '@folio/lib';
import { useTranslation } from 'react-i18next';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

import {
  Alert,
  AlertTitle,
  FunnelStepHeader,
  FunnelWithHelper,
  SectionedFormBuilder,
  areFieldsDirty,
  buildFormValuesFromFields,
  getResolver,
  useStepperContext,
} from '@folio/components';
import { FormBuilderFooter } from '@folio/components/forms/form-builder/FormBuilderFooter';
import {
  clientWithFork,
  configControllerGetPublicProductConfigQueryOptions,
  firstPaymentEnum,
  getNewBranchIdAfterFork,
  InvoicingConfigDto,
  keyValueFieldsToObject,
  policyControllerGetPolicyQueryOptions,
  PolicyDto,
  usePolicyControllerEditPolicy,
  type InvoiceFrequencyEnumKey,
} from 'korint-sdk';
import { useForm, useFormState } from 'react-hook-form';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Field } from 'korint-sdk/react';
import { uniqBy } from 'lodash';
import { AlertTriangleIcon } from 'lucide-react';
import { Stack } from '@folio/components/ui/flex';

enum EarlyPaymentAmountSpecialOptions {
  FULL = '-2',
  NONE = '-1',
}

function getEarlyPaymentAmountValue(config: InvoicingConfigDto) {
  return config.firstPayment === firstPaymentEnum.FULL_AT_SIGNATURE ||
    config.firstPayment === firstPaymentEnum.FULL_AT_START_DATE
    ? EarlyPaymentAmountSpecialOptions.FULL
    : config.firstPayment === firstPaymentEnum.NONE
      ? EarlyPaymentAmountSpecialOptions.NONE
      : config.earlyPayment?.amount.toString();
}

type BillingSelectionFunnelModuleProps = {
  alertMessage?: React.ReactNode;
  title?: string;
  subtitle?: string;
  frequencyIcons?: Record<string, React.ComponentType<{ className?: string }>>;
  earlyPaymentDisplay?: 'toggle';
  helper?: React.ReactNode;
};

export function BillingSelectionFunnelModule({
  alertMessage,
  title,
  subtitle,
  frequencyIcons,
  earlyPaymentDisplay,
  helper,
}: BillingSelectionFunnelModuleProps) {
  const { t } = useTranslation();
  const { policyId, branchId } = useAssertedParams();

  const {
    data: { productId },
  } = useSuspenseQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
  });

  const {
    data: { availableConfigs: allAvailableConfigs },
  } = useSuspenseQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId),
    select: ({ billing }) => billing,
  });

  const { mutateAsync: updatePolicy } = usePolicyControllerEditPolicy({
    client: { client: clientWithFork },
  });

  const {
    data: policy,
    isLoading,
    refetch,
  } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const [availableConfigs, setAvailableConfigs] = useState(allAvailableConfigs);

  const invoicingFrequencyField = useMemo<Field>(
    () => ({
      name: 'invoicingFrequency' as const,
      type: 'radio' as const,
      // Render as selectable cards when the tenant provides per-frequency icons.
      display: frequencyIcons ? ('cards' as const) : undefined,
      required: true,
      icons: frequencyIcons,
      options: uniqBy(
        allAvailableConfigs.map(({ frequency }) => ({
          label: t([
            `billingSelection.frequency.${frequency}_${productId}`,
            `billingSelection.frequency.${frequency}`,
          ]),
          value: frequency.toString(),
        })),
        'value',
      ),
      apiDataAccessor: (policy: PolicyDto) => policy.invoicingConfig.frequency,
    }),
    [allAvailableConfigs, t, productId, frequencyIcons],
  );

  const earlyPaymentAmountField = useMemo(() => {
    return {
      name: 'earlyPaymentAmount' as const,
      type: 'radio' as const,
      required: true,
      display: earlyPaymentDisplay,
      options: uniqBy(
        availableConfigs.map(config => {
          const value = getEarlyPaymentAmountValue(config);

          if (!value) {
            throw new Error('Invalid early payment amount value');
          }

          if (value === EarlyPaymentAmountSpecialOptions.FULL) {
            return { value, label: t('billingSelection.options.fullPayment') };
          }

          if (value === EarlyPaymentAmountSpecialOptions.NONE) {
            return { value, label: t('billingSelection.options.noPayment') };
          }

          const earlyPaymentAmount = Number(value);
          const frequency = config.frequency;

          return {
            value,
            label:
              earlyPaymentAmount === 0
                ? t([
                    `billingSelection.earlyPayment.currentInstallment_${productId}_${frequency}`,
                    `billingSelection.earlyPayment.currentInstallment_${productId}`,
                    'billingSelection.earlyPayment.currentInstallment',
                  ])
                : t(
                    [
                      `billingSelection.earlyPayment.withAdditionalInstallments_${productId}_${frequency}`,
                      `billingSelection.earlyPayment.withAdditionalInstallments_${productId}`,
                      'billingSelection.earlyPayment.withAdditionalInstallments',
                    ],
                    { count: earlyPaymentAmount },
                  ),
          };
        }),
        'value',
      ),
      apiDataAccessor: (policy: PolicyDto) => getEarlyPaymentAmountValue(policy.invoicingConfig),
    };
  }, [availableConfigs, t, productId, earlyPaymentDisplay]);

  const hasMultipleEarlyPaymentOptions = earlyPaymentAmountField.options.length > 1;

  const fields = useMemo(
    () => [invoicingFrequencyField, earlyPaymentAmountField],
    [invoicingFrequencyField, earlyPaymentAmountField],
  );

  const visibleFields = useMemo(
    () =>
      hasMultipleEarlyPaymentOptions
        ? [invoicingFrequencyField, earlyPaymentAmountField]
        : [invoicingFrequencyField],
    [invoicingFrequencyField, earlyPaymentAmountField, hasMultipleEarlyPaymentOptions],
  );

  const resolver = useMemo(() => getResolver(fields), [fields]);

  const initialValues = useMemo(() => buildFormValuesFromFields(fields, policy), [fields, policy]);

  const form = useForm({
    resolver,
    values: initialValues,
  });

  const selectedInvoicingFrequency = form.watch('invoicingFrequency') as string;
  const currentEarlyPaymentAmount = form.watch('earlyPaymentAmount') as string | undefined;

  // Keep a ref to avoid including initialValues in effect deps, which would cause
  // an infinite loop: setAvailableConfigs → new filteredConfigs ref → re-render →
  // earlyPaymentAmountField/fields/initialValues all get new refs → effect re-runs
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialValues intentionally excluded via ref (see comment above)
  useEffect(() => {
    const filteredConfigs = allAvailableConfigs.filter(
      config => config.frequency === selectedInvoicingFrequency,
    );

    setAvailableConfigs(filteredConfigs);

    if (filteredConfigs.length > 0) {
      const validValues = filteredConfigs.map(config => getEarlyPaymentAmountValue(config));

      const initialFrequency = initialValuesRef.current.invoicingFrequency as string | undefined;
      const initialEarlyPayment = initialValuesRef.current.earlyPaymentAmount as string | undefined;
      if (
        currentEarlyPaymentAmount === undefined &&
        selectedInvoicingFrequency === initialFrequency &&
        initialEarlyPayment !== undefined &&
        validValues.includes(initialEarlyPayment)
      ) {
        form.setValue('earlyPaymentAmount', initialEarlyPayment, { shouldDirty: false });
      } else if (
        currentEarlyPaymentAmount === undefined ||
        !validValues.includes(currentEarlyPaymentAmount)
      ) {
        form.setValue('earlyPaymentAmount', validValues[0], { shouldDirty: true });
      }
    }
  }, [selectedInvoicingFrequency, allAvailableConfigs, currentEarlyPaymentAmount, form]);

  const stepper = useStepperContext();

  const stepperRef = useRef(stepper);
  stepperRef.current = stepper;
  const { isDirty } = useFormState({ control: form.control });
  // biome-ignore lint/correctness/useExhaustiveDependencies: stepperRef.current avoids stale closure without creating a dep cycle
  useEffect(() => {
    if (isDirty) stepperRef.current?.invalidateFutureSteps?.();
  }, [isDirty]);

  const onSubmit = form.handleSubmit(async data => {
    const { dirtyFields } = form.formState;
    const isDirty = areFieldsDirty(fields, dirtyFields);

    let mutationBranchId: string | undefined = branchId;

    if (isDirty) {
      const matchingConfig = allAvailableConfigs.find(
        config =>
          config.frequency === (data.invoicingFrequency as string) &&
          getEarlyPaymentAmountValue(config) === data.earlyPaymentAmount,
      );

      if (!matchingConfig) {
        throw new Error(
          `No billing config found for frequency=${data.invoicingFrequency} earlyPaymentAmount=${data.earlyPaymentAmount}`,
        );
      }

      const response = await updatePolicy({
        policyId,
        params: { branchId },
        data: {
          invoicingConfig: {
            firstPayment: matchingConfig.firstPayment,
            ...(matchingConfig.earlyPayment
              ? {
                  earlyPayment: {
                    amount: Number(data.earlyPaymentAmount),
                    unit: 'MONTH', // @debt, this is a useless attribute we have not removed yet
                  },
                }
              : {}),
            frequency: data.invoicingFrequency as InvoiceFrequencyEnumKey,
            timing: 'IN_ADVANCE',
          },
        },
      });

      const newBranchId = getNewBranchIdAfterFork(response);
      if (newBranchId) {
        mutationBranchId = newBranchId;
      }
    }

    await refetch();

    stepper.next(mutationBranchId ? { branchId: mutationBranchId } : undefined);
  });

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={title ?? t('funnelSteps.billingSelection.title')}
        subtitle={subtitle ?? t('funnelSteps.billingSelection.subtitle', '')}
      />
      <FunnelWithHelper helper={helper}>
        {alertMessage && (
          <Alert variant="info">
            <AlertTitle className="flex items-center gap-4 pt-1">
              <AlertTriangleIcon />
              {alertMessage}
            </AlertTitle>
          </Alert>
        )}
        <SectionedFormBuilder
          form={form}
          isLoading={isLoading}
          externalData={policy}
          sections={[{ title: t('billingSelection.title'), fields: visibleFields }]}
        />
      </FunnelWithHelper>
      <FormBuilderFooter
        mode={'stepper'}
        onSubmit={onSubmit}
        form={form}
        showPreviousButton={true}
      />
    </Stack>
  );
}
